/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Badge, Button, Form as NXUIForm, Theme } from '@nexigendigital/nxui'
import { useAddPromoCodeToCartMutation, useRemovePromoCodeFromCartMutation } from 'api/shop'
import { useAppSelector } from 'store/hooks'
import { getMemoizedPromoCodes } from 'store/slices/shopSlice'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function PromoForm() {
    const promoCodes = useAppSelector(getMemoizedPromoCodes)
    const formRef = useRef<FormikProps<{ code: string }>>(null)
    const { pathname } = useLocation()
    const { cart } = useAppSelector((state) => state.shop)
    const [addPromoCodeToCart, { isLoading, isError, isSuccess }] = useAddPromoCodeToCartMutation()
    const [removePromoCodeFromCart] = useRemovePromoCodeFromCartMutation()

    useEffect(() => {
        if ((isError || isSuccess) && formRef.current) {
            formRef.current.setSubmitting(false)
            formRef.current.resetForm()
        }
    }, [isError, isSuccess])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    if (!cart) {
        return null
    }

    return (
        <Formik
            innerRef={formRef}
            initialValues={{
                code: ''
            }}
            validationSchema={
                Yup.object({
                    code: Yup.string()
                })
            }
            onSubmit={({ code }) => {
                addPromoCodeToCart({ uuid: cart.uuid, code })
            }}
        >
            {() => (
                <StyledPromo>
                    {pathname === '/checkout' &&
                        (
                            <Input as={Form}>
                                <Field
                                    label={'Promo Code'}
                                    name={'code'}
                                    type={'text'}
                                    disabled={isLoading}
                                    component={NXUIForm.InputField}
                                />
                                <Button
                                    type={'submit'}
                                    color={'primary'}
                                    disabled={isLoading}
                                    loading={isLoading ? 'Applying' : false}
                                >
                                    Apply
                                </Button>
                            </Input>
                        )
                    }
                    <PromoCodes>
                        {promoCodes.length >= 1 &&
                            promoCodes.map((promoCode) => {
                                return (
                                    <Badge key={promoCode} color={'alternative'} onClick={() => {
                                        removePromoCodeFromCart({
                                            uuid: cart.uuid,
                                            code: promoCode
                                        })
                                    }}>
                                        <span>{String(promoCode).toUpperCase()}</span>
                                    </Badge>
                                )
                            })
                        }
                    </PromoCodes>
                </StyledPromo>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledPromo = styled.div`
    display: flex;
    flex-direction: column;
`

const Input = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    ${props => Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm'])} {
        flex-direction: column;
    }

    > div {
        width: 100%;
    }
    
    > button {
        ${props => Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm'])} {
            width: 100%;
        }
    }
`

const PromoCodes = styled.div`
    margin: 0 24px 20px 24px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`
